import React, { useEffect, useRef } from 'react';

// Full-viewport blocker shown while a server request is in flight
// (login / logout / save / cancel). Rendered as a native `<dialog>`
// opened via `showModal()` so the browser puts it in the Top Layer —
// paints ABOVE every stacking context (sticky sidebars, headers,
// portaled chat dock, other modals) without any `z-index`.
//
// Top Layer also gives us a free focus trap and `inert` background —
// the user can't Tab or click through to the page underneath while the
// request is in flight. `cancel` (Esc) is suppressed so the overlay
// stays up for the full request lifecycle.
export default function BusyOverlay({ message = 'Just a moment' }) {
    const dialogRef = useRef(null);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog || typeof dialog.showModal !== 'function') return undefined;

        if (!dialog.open) dialog.showModal();

        // Block Esc-to-close — the overlay represents an in-flight
        // network request the customer can't cancel from this UI.
        const onCancel = (e) => e.preventDefault();
        dialog.addEventListener('cancel', onCancel);

        return () => {
            dialog.removeEventListener('cancel', onCancel);
            if (dialog.open) dialog.close();
        };
    }, []);

    // Trim trailing ellipsis — callers often pass "Saving…" but the
    // three bouncing dots already carry the ongoing-action signal, so
    // the ellipsis is redundant noise inside the label.
    const label = String(message).replace(/[.…]+\s*$/, '');

    return (
        <dialog
            ref={dialogRef}
            className="bidding-busy-overlay"
            aria-busy="true"
            aria-live="polite"
        >
            <div className="bidding-busy-overlay__card">
                <div className="bidding-busy-overlay__dots" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                </div>
                <p className="bidding-busy-overlay__message">{label}</p>
            </div>
        </dialog>
    );
}
